import React, { Component } from "react";
import homelogo from "../../images/homeimg.jpg";
import { post } from "../../utils/sysAxios";
import { Chart } from "primereact/chart";
import { withTranslation } from "react-i18next";
import "../../styles/charts.css";
import sysJwt from "../../utils/sysJwt";

const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#14B8A6", "#F97316", "#6366F1",
];

const shared = {
  maintainAspectRatio: false,
  responsive: true,
  animation: { duration: 600 },
  plugins: {
    title: { display: false },
    tooltip: {
      backgroundColor: "#1F2937",
      titleFont: { size: 11, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
      callbacks: {},
    },
  },
};

const legendBottom = {
  position: "bottom",
  labels: { boxWidth: 10, padding: 14, font: { size: 11 }, color: "#6B7280" },
};

const scales = {
  x: {
    grid: { display: false },
    ticks: { font: { size: 10 }, color: "#9CA3AF" },
    border: { display: false },
  },
  y: {
    grid: { color: "#F3F4F6", drawBorder: false },
    ticks: { font: { size: 10 }, color: "#9CA3AF" },
    border: { display: false },
    beginAtZero: true,
  },
};

const chartStyle = { width: "100%", height: "100%" };

function Card({ icon, title, wide, half, children }) {
  let cls = "hc-card";
  if (wide) cls += " hc-card--wide";
  if (half) cls += " hc-card--half";
  return (
    <div className={cls}>
      <div className="hc-title">
        <i className={`fa ${icon}`} />
        {title}
      </div>
      <div className="hc-chart-wrap">{children}</div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div className="hc-kpi" style={{ "--kpi-color": color }}>
      <div className="hc-kpi-icon"><i className={`fa ${icon}`} /></div>
      <div className="hc-kpi-body">
        <div className="hc-kpi-value">{typeof value === "number" ? value.toLocaleString() : value}</div>
        <div className="hc-kpi-label">{label}</div>
        {sub && <div className="hc-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

class Home extends Component {
  state = {
    kpi: null,
    paymentsPerMethod: [],
    last15DayPayments: [],
    billStatus: [],
    usersStatus: [],
    paymentsPerService: [],
    monthlyRevenue: [],
    monthlyBills: [],
  };

  componentDidMount() {
    this.getData();
  }

  getData() {
    if(sysJwt.isAuthorized(["CB91"])){
    post("/api/data/GetHomeCharts", null, this.props.updateIsLoading).then(
      (apiResponse) => {
        const d = apiResponse.data.respData;
        this.setState({
          kpi: d.kpi,
          paymentsPerMethod: d.paymentsPerMethod,
          last15DayPayments: d.last15DayPayments,
          billStatus: d.billStatus,
          usersStatus: d.usersStatus,
          paymentsPerService: d.paymentsPerService,
          monthlyRevenue: d.monthlyRevenue,
          monthlyBills: d.monthlyBills,
        });
      }
    );
  }
  }

  render() {
    const { t } = this.props;
    const {
      kpi, usersStatus, billStatus, paymentsPerMethod,
      last15DayPayments, paymentsPerService, monthlyRevenue, monthlyBills,
    } = this.state;

    if (!kpi) {
      return (
        <div className="home-logo text-center" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <img src={homelogo} width={1300} height={500} alt="HOME" />
        </div>
      );
    }

    return (
      <div className="hc-dashboard fade-in">

        {/* ── KPI row ──────────────────────────────────────────────── */}
        <div className="hc-kpi-grid">
          <KpiCard icon="fa-home" label={t("home.totalUnits")} value={kpi.totalUnits} color="#3B82F6" />
          <KpiCard icon="fa-users" label={t("home.activeOwners")} value={kpi.totalActiveOwners}
            sub={`${kpi.totalOwners} ${t("home.totalLabel")}`} color="#10B981" />
          <KpiCard icon="fa-money" label={t("home.revenueThisMonth")}
            value={kpi.totalPaymentsThisMonth.toLocaleString()} color="#F59E0B" />
          <KpiCard icon="fa-exclamation-circle" label={t("home.unpaidBillsAmount")}
            value={kpi.totalUnpaidBillsAmount.toLocaleString()}
            sub={`${kpi.totalUnpaidBillsCount} ${t("home.billsLabel")}`} color="#EF4444" />
        </div>

        {/* ── Row 2: 3 small charts ────────────────────────────────── */}
        <div className="hc-grid">

          {/* Doughnut — Users */}
          <Card icon="fa-user-circle" title={t("home.ownerStatus")}>
            <Chart type="doughnut" style={chartStyle}
              data={{
                labels: usersStatus.map((e) => e.KEY),
                datasets: [{
                  data: usersStatus.map((e) => e.VALUE),
                  backgroundColor: usersStatus.map((e, i) => e.COLOR || PALETTE[i % PALETTE.length]),
                  borderWidth: 3, borderColor: "#fff", hoverOffset: 8,
                }],
              }}
              options={{
                ...shared, cutout: "68%",
                plugins: { ...shared.plugins, legend: legendBottom },
              }}
            />
          </Card>

          {/* Bar — Bills this month */}
          <Card icon="fa-file-text-o" title={t("home.billsThisMonth")}>
            <Chart type="bar" style={chartStyle}
              data={{
                labels: billStatus.map((e) => e.KEY),
                datasets: [{
                  data: billStatus.map((e) => e.VALUE),
                  backgroundColor: billStatus.map((e, i) => e.COLOR || PALETTE[i % PALETTE.length]),
                  borderRadius: 8, borderSkipped: false, barThickness: 40,
                }],
              }}
              options={{
                ...shared,
                plugins: { ...shared.plugins, legend: { display: false } },
                scales,
              }}
            />
          </Card>

          {/* Pie — Payment Methods */}
          <Card icon="fa-credit-card" title={t("home.paymentMethods30Days")}>
            <Chart type="pie" style={chartStyle}
              data={{
                labels: paymentsPerMethod.map((e) => e.KEY),
                datasets: [{
                  data: paymentsPerMethod.map((e) => e.VALUE),
                  backgroundColor: PALETTE.slice(0, paymentsPerMethod.length),
                  borderWidth: 3, borderColor: "#fff", hoverOffset: 8,
                }],
              }}
              options={{
                ...shared,
                plugins: { ...shared.plugins, legend: legendBottom },
              }}
            />
          </Card>
        </div>

        {/* ── Row 3: full-width line + horizontal bar ──────────────── */}
        <div className="hc-grid hc-grid--2">

          {/* Area line — 15-day payment trend */}
          <Card icon="fa-line-chart" title={t("home.paymentCount15Days")}>
            <Chart type="line" style={chartStyle}
              data={{
                labels: last15DayPayments.map((e) => e.KEY),
                datasets: [{
                  label: t("home.paymentsDataset"),
                  data: last15DayPayments.map((e) => e.VALUE),
                  borderColor: "#3B82F6",
                  backgroundColor: "rgba(59,130,246,0.10)",
                  fill: true, tension: 0.45,
                  pointBackgroundColor: "#3B82F6",
                  pointBorderColor: "#fff", pointBorderWidth: 2,
                  pointRadius: 4, pointHoverRadius: 7, borderWidth: 2,
                }],
              }}
              options={{
                ...shared,
                plugins: { ...shared.plugins, legend: { display: false } },
                scales,
              }}
            />
          </Card>

          {/* Horizontal bar — payments by service */}
          <Card icon="fa-bar-chart" title={t("home.revenueByService30Days")}>
            <Chart type="bar" style={chartStyle}
              data={{
                labels: paymentsPerService.map((e) => e.KEY),
                datasets: [{
                  label: t("home.amountDataset"),
                  data: paymentsPerService.map((e) => e.VALUE),
                  backgroundColor: PALETTE.slice(0, paymentsPerService.length),
                  borderRadius: 6, borderSkipped: false,
                }],
              }}
              options={{
                ...shared, indexAxis: "y",
                plugins: { ...shared.plugins, legend: { display: false } },
                scales: {
                  x: { ...scales.x, grid: { color: "#F3F4F6" } },
                  y: { ...scales.y, grid: { display: false } },
                },
              }}
            />
          </Card>
        </div>

        {/* ── Row 4: monthly revenue (wide) + stacked bills (wide) ─── */}
        <div className="hc-grid hc-grid--2">

          {/* Area line — 6-month revenue */}
          <Card icon="fa-area-chart" title={t("home.monthlyRevenue6Months")}>
            <Chart type="line" style={chartStyle}
              data={{
                labels: monthlyRevenue.map((e) => e.KEY),
                datasets: [{
                  label: t("home.revenueDataset"),
                  data: monthlyRevenue.map((e) => e.VALUE),
                  borderColor: "#10B981",
                  backgroundColor: "rgba(16,185,129,0.10)",
                  fill: true, tension: 0.4,
                  pointBackgroundColor: "#10B981",
                  pointBorderColor: "#fff", pointBorderWidth: 2,
                  pointRadius: 4, pointHoverRadius: 7, borderWidth: 2,
                }],
              }}
              options={{
                ...shared,
                plugins: { ...shared.plugins, legend: { display: false } },
                scales,
              }}
            />
          </Card>

          {/* Stacked bar — bills paid vs unpaid per month */}
          <Card icon="fa-tasks" title={t("home.billsPaidVsUnpaid6Months")}>
            <Chart type="bar" style={chartStyle}
              data={{
                labels: monthlyBills.map((e) => e.KEY),
                datasets: [
                  {
                    label: t("home.paidDataset"),
                    data: monthlyBills.map((e) => e.PAID),
                    backgroundColor: "#10B981",
                    borderRadius: 4, borderSkipped: false, stack: "bills",
                  },
                  {
                    label: t("home.unpaidDataset"),
                    data: monthlyBills.map((e) => e.UNPAID),
                    backgroundColor: "#EF4444",
                    borderRadius: 4, borderSkipped: false, stack: "bills",
                  },
                ],
              }}
              options={{
                ...shared,
                plugins: {
                  ...shared.plugins,
                  legend: {
                    position: "bottom",
                    labels: { boxWidth: 10, padding: 14, font: { size: 11 }, color: "#6B7280" },
                  },
                },
                scales,
              }}
            />
          </Card>
        </div>

      </div>
    );
  }
}

export default withTranslation()(Home);
